import { DateFormatter } from '@repo/toolkit/date'
import { DateRangeInput } from '@repo/ui/date-range-input'
import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'

import NumberInput from '../../../../../../packages/ui/components/atoms/input/number-input'
import { getPeriods } from '../../../api/periods/get-periods'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { MissionTrackingFilters } from '../../../lib/types/models/mission-tracking/mission-tracking'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'

interface Props {
  filter?: MissionTrackingFilters
  onFilterChange: (filter: MissionTrackingFilters) => void
}

export function MissionTrackingFilter({ filter, onFilterChange }: Props) {
  function setFilter<K extends keyof MissionTrackingFilters>(
    key: K,
    value: MissionTrackingFilters[K]
  ) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { t } = useTranslate()

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  return (
    <div className={'flex flex-col gap-3'}>
      <MultiSelectInput
        variant="primary"
        label={'Qualifications requises'}
        options={Object.values(StaffQualificationEnum).map((type) => ({
          label: t(staffQualificationEnumMapping[type].label),
          value: type
        }))}
        onChange={(type) => setFilter('qualifications', type)}
        selectedOptions={
          filter?.qualifications?.map((type) => ({
            label: t(staffQualificationEnumMapping[type].label),
            value: type
          })) ?? []
        }
      />

      <DateRangeInput
        label={'Date du contrat'}
        value={
          filter?.contractDateRange?.startDate && filter?.contractDateRange?.endDate
            ? {
                from: DateFormatter.toDate(filter.contractDateRange.startDate),
                to: DateFormatter.toDate(filter.contractDateRange.endDate)
              }
            : undefined
        }
        onClearDate={() => onFilterChange({ ...filter, contractDateRange: undefined })}
        onDateRangeChange={(dateRange) => {
          if (dateRange?.from && dateRange?.to)
            onFilterChange({
              ...filter,
              contractDateRange: {
                startDate: DateFormatter.toDateFormatted(dateRange?.from),
                endDate: DateFormatter.toDateFormatted(dateRange?.to)
              }
            })
        }}
      />

      <SelectInput
        label={'Période'}
        options={
          periods?.data.map((period) => ({
            label: period.name,
            value: period.id
          })) ?? []
        }
        selectedOption={getSelectedOption(
          filter?.periodId,
          periods?.data.find((period) => period.id === filter?.periodId)?.name
        )}
        onChange={(periodId) => setFilter('periodId', periodId)}
      />

      <DateStringInput
        mode="over"
        label="Date de la mission"
        value={filter?.date}
        onChange={(date) => setFilter('date', date)}
      />

      <NumberInput
        label="Nombre de missions"
        value={filter?.missionsCount}
        onChange={(e) => setFilter('missionsCount', +e.target.value)}
      />

      <SelectInput<boolean>
        label={'Documents validés'}
        options={[
          { label: 'Oui', value: true },
          { label: 'Non', value: false }
        ]}
        selectedOption={
          filter?.documents !== undefined
            ? {
                label: filter?.documents ? 'Oui' : 'Non',
                value: filter?.documents
              }
            : undefined
        }
        onChange={(option) => setFilter('documents', option)}
      />

      <SelectInput<boolean>
        label={'Contrat envoyé'}
        options={[
          { label: 'Oui', value: true },
          { label: 'Non', value: false }
        ]}
        selectedOption={
          filter?.contractSentAt !== undefined
            ? {
                label: filter?.contractSentAt ? 'Oui' : 'Non',
                value: filter?.contractSentAt
              }
            : undefined
        }
        onChange={(option) => setFilter('contractSentAt', option)}
      />

      <SelectInput<boolean>
        label={'DPAE téléchargé'}
        options={[
          { label: 'Oui', value: true },
          { label: 'Non', value: false }
        ]}
        selectedOption={
          filter?.dpaeDownloadAt !== undefined
            ? {
                label: filter?.dpaeDownloadAt ? 'Oui' : 'Non',
                value: filter?.dpaeDownloadAt
              }
            : undefined
        }
        onChange={(option) => setFilter('dpaeDownloadAt', option)}
      />

      <SelectInput<boolean>
        label={'DPAE rempli'}
        options={[
          { label: 'Oui', value: true },
          { label: 'Non', value: false }
        ]}
        selectedOption={
          filter?.dpaeFilledAt !== undefined
            ? {
                label: filter?.dpaeFilledAt ? 'Oui' : 'Non',
                value: filter?.dpaeFilledAt
              }
            : undefined
        }
        onChange={(option) => setFilter('dpaeFilledAt', option)}
      />
    </div>
  )
}
